import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import AuthService from '../services/auth.service';
import StorageService, { STORAGE_KEYS } from './storage.util';
import { config } from '../config';

// Base configuration for axios
const axiosInstance = axios.create({
  baseURL: config.auth_api_url,
  timeout: 90000,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    const token = StorageService.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Skip session validation for login and signup endpoints
      const requestUrl = error.config?.url || '';
      const isLoginOrSignupRequest = requestUrl.includes('/auth/login') ||
                                   requestUrl.includes('/auth/signup') ||
                                   requestUrl.includes('signup-without-payment') ||
                                   requestUrl.includes('confirm-signup');
      if (!isLoginOrSignupRequest) {
        try {
          const isValid = await AuthService.validateSession();
          if (!isValid) {
            AuthService.logout();
            window.location.href = '/login';
          }
        } catch (refreshError) {
          AuthService.logout();
          window.location.href = '/login';
        }
      }
    }

    return Promise.reject(error);
  }
);

// Generic GET request function
export const get = async <T>(
  url: string, 
  config?: AxiosRequestConfig
): Promise<T> => {
  try {
    const response: AxiosResponse<T> = await axiosInstance.get(url, config);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

// Generic POST request function
export const post = async <T>(
  url: string, 
  data?: any, 
  config?: AxiosRequestConfig
): Promise<T> => {
  try {
    const response: AxiosResponse<T> = await axiosInstance.post(url, data, config);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Generic PUT request function
export const put = async <T>(
  url: string, 
  data?: any, 
  config?: AxiosRequestConfig
): Promise<T> => {
  try {
    const response: AxiosResponse<T> = await axiosInstance.put(url, data, config);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

// Generic DELETE request function
export const del = async <T>(
  url: string, 
  config?: AxiosRequestConfig
): Promise<T> => {
  try {
    const response: AxiosResponse<T> = await axiosInstance.delete(url, config);
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

// Error handling function
const handleApiError = (error: any) => {
  if (error.response) {
    // Server responded with error status
    console.error('API Error:', error.response.data);
  } else if (error.request) {
    // Request was made but no response received
    console.error('Network Error:', error.request);
  } else {
    // Something else happened while setting up the request
    console.error('Error:', error.message);
  }
};

// Types for API responses
export interface ApiResponse<T> {
  data: T;
  message?: string;
  status: number;
}
